import { useState } from 'react'
import { Alert, Button, Checkbox, Group, Modal, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconAlertTriangle } from '@tabler/icons-react'
import { isAxiosError } from 'axios'

import { pedirRecomeco, type RoboFrota } from '@/api/robo'
import { useUsuariosParaRobo } from './AtribuirDono'
import { nomeOuFallback } from './nomeDoRobo'

/**
 * A recusa do reset que vira frase, e ela é da conversa (spec 3.6.1): o backend
 * recusa ANTES de escrever, então nada do reset aconteceu — e dizer isso é o que
 * evita a pessoa achar que o robô já foi zerado. Anexo não recusa mais: desde
 * 03/10/2026 ele é apagado junto com a conversa.
 */
const FRASES_409: Record<string, string> = {
  conversa_com_registro:
    'A conversa está ligada a um pedido de infraestrutura e não pode ser apagada. '
    + 'Nada foi zerado — desmarque a caixa para recomeçar mantendo a conversa.',
}

function fraseDoErro(e: unknown): string {
  if (isAxiosError(e) && e.response?.status === 409) {
    const detail = (e.response.data as { detail?: unknown })?.detail
    if (typeof detail === 'string' && FRASES_409[detail]) return FRASES_409[detail]
  }
  return 'Não foi possível pedir o recomeço. Tente de novo.'
}

/**
 * "Começar de novo": o robô volta ao estado de fábrica e conduz a configuração
 * desde a chegada (spec 2026-09-28-robo-recepcao-conduzida, 3.6 e 3.6.1).
 *
 * A confirmação existe porque o efeito é FÍSICO e não tem desfazer pelo Console:
 * depois de apagar a rede, o robô não busca mais o snapshot, e quem estiver longe
 * dele fica sem caminho de volta — o próximo passo é na rede que ele abre.
 *
 * A caixa da conversa só aparece com dono, e nasce DESMARCADA: a conversa é uma
 * por dono, não por robô, e apagá-la leva o histórico dele com qualquer robô.
 */
export function ComecarDeNovo(
  { robo, semSinal, onMudou }: {
    robo: Pick<RoboFrota, 'public_id' | 'name'> & Partial<Pick<RoboFrota, 'owner_user_id'>>
    semSinal: boolean
    onMudou?: () => void
  },
) {
  const [aberto, ctl] = useDisclosure(false)
  const [ordem, setOrdem] = useState<number | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [apagarConversa, setApagarConversa] = useState(false)

  const dono = robo.owner_user_id ?? null
  const { data: usuarios } = useUsuariosParaRobo(dono !== null)
  const u = dono === null ? undefined : usuarios?.find((x) => x.id === dono)
  const nomeDoDono = u?.full_name || u?.email || (dono === null ? null : `usuário #${dono}`)

  const abrir = () => {
    setApagarConversa(false)
    setErro(null)
    ctl.open()
  }

  return (
    <Stack gap="sm">
      <Text size="sm">
        O robô esquece as redes Wi-Fi que guardou e recomeça a recepção: ele abre a
        rede dele, mostra a senha no visor e conduz a configuração de novo. No Console
        ele também volta ao padrão de fábrica: nome, dono, escopo, aparência e ajustes
        de tela são apagados. Ficam a identidade e a credencial dele.
      </Text>
      {/* A ordem viaja pelo snapshot: robô que nao esta buscando nao a recebe, e
          fica esperando pelo proximo boot com rede. Dizer isso ANTES evita o
          clique que "nao fez nada". */}
      {semSinal && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          Este robô não está falando com o Console agora. A ordem fica guardada e
          só chega quando ele voltar.
        </Alert>
      )}
      {ordem !== null && (
        <Alert color="teal">
          Pedido enviado (ordem {ordem}). O robô recomeça na próxima vez que
          consultar o Console.
        </Alert>
      )}
      <Group>
        <Button variant="default" color="red" onClick={abrir}>
          Começar de novo
        </Button>
      </Group>

      <Modal opened={aberto} onClose={ctl.close} title="Começar de novo">
        <Stack gap="sm">
          {/* Confirmacao NOMEANDO o robo: numa frota de nomes parecidos, resetar
              o errado manda alguem ate' o aparelho. */}
          <Text size="sm">
            <strong>{nomeOuFallback(robo)}</strong> vai esquecer as redes que guardou e
            recomeçar a configuração desde o começo. Nome, dono, escopo, aparência e
            ajustes de tela voltam ao padrão.
          </Text>
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              Atenção: o robô vai sair da rede e só volta pela rede dele — você
              precisa estar perto dele.
            </Text>
          </Alert>
          {nomeDoDono && (
            <Checkbox
              checked={apagarConversa}
              onChange={(ev) => setApagarConversa(ev.currentTarget.checked)}
              label={`Apagar também a conversa de ${nomeDoDono} com o robô`}
              description="A conversa é uma só para todos os robôs dessa pessoa. Os arquivos anexados nela também são apagados."
            />
          )}
          {/* O erro fica DENTRO do diálogo: ele continua aberto na falha, e um
              alerta atrás dele seria um erro que ninguém vê. */}
          {erro && <Alert color="red">{erro}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={ctl.close}>Cancelar</Button>
            <Button
              color="red"
              loading={enviando}
              onClick={async () => {
                setEnviando(true)
                setErro(null)
                try {
                  setOrdem(await pedirRecomeco(robo.public_id,
                    { apagarConversa: nomeDoDono !== null && apagarConversa }))
                  ctl.close()
                  onMudou?.()
                } catch (e) {
                  setErro(fraseDoErro(e))
                } finally {
                  setEnviando(false)
                }
              }}
            >
              Começar de novo
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
